import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Alert, Badge, Snackbar } from "@mui/material";
import { ShoppingCart } from "react-feather";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import PageNotFound from "../page-not-found/PageNotFound";
import ProductCatalogue from "./components/ProductCatalogue";
import ProductDetail from "./components/ProductDetail";
import CartView from "./components/CartView";
import CartDrawer from "./components/CartDrawer";
import AnnouncementBar from "./components/AnnouncementBar";
import { addToCart, isCustomer, openCartDrawer, shopEnabled, useCartCount } from "./shopApi";
import styles from "./shop.module.css";

// Public Shop Now on a tenant site (/shop, /shop/:id, /shop/cart).
// Anyone can browse and fill a cart; checkout asks guests to sign in or sign up.
const Shop = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isCartPage = pathname.replace(/\/$/, "") === "/shop/cart";
  const { websiteData } = useWebsite();
  useSiteBranding(isCartPage ? "Your cart" : "Shop", websiteData);
  const loggedIn = isCustomer();
  const enabled = !!websiteData && shopEnabled(websiteData);
  const cartCount = useCartCount(enabled);
  const [addingId, setAddingId] = useState(0);
  const [error, setError] = useState("");

  if (!enabled) return <PageNotFound />;

  const primary = websiteData?.home_config?.primaryColor || "#ef233c";
  const secondary = websiteData?.home_config?.secondaryColor || "#0a2a52";
  const logo = websiteData?.home_config?.logoURL || "/images/logo.svg";

  const onAdd = (item, qty) => {
    setAddingId(item.id);
    addToCart(item.id, qty).then(() => {
      setAddingId(0);
      openCartDrawer();
    }).catch((e) => {
      setAddingId(0);
      setError(e.message);
    });
  };

  return (<div style={{ "--primary-color": primary, "--secondary-color": secondary, minHeight: "100vh", background: "#fafbfc" }}>
    <AnnouncementBar text={websiteData?.shop_announcement} />
    <header className={styles.PublicHeader}>
      <div className={styles.PublicHeaderInner}>
        <Link to="/" className={styles.PublicBrand}>
          <img src={logo} alt="" />
          <span>{websiteData?.company_name}</span>
        </Link>
        <nav className={styles.PublicNav}>
          <Link to="/shop">Shop Now</Link>
          <Link to="/" className={styles.PublicNavHideMobile}>Home</Link>
          <Link to="/track-booking" className={styles.PublicNavHideMobile}>Track</Link>
          {loggedIn ? <Link to="/customer/my-bookings">My account</Link> : <Link to={"/sign-in?redirect=" + encodeURIComponent("/customer/shop")}>Sign In</Link>}
          <Link to="/shop/cart" onClick={(e) => { e.preventDefault(); openCartDrawer(); }} aria-label={`Cart, ${cartCount} items`}>
            <Badge badgeContent={cartCount} color="error"><ShoppingCart size={20} /></Badge>
          </Link>
        </nav>
      </div>
    </header>

    <div className={styles.Page}>
      {isCartPage && (<>
        <div className={styles.PageHead}>
          <h1 className={styles.PageTitle}>Your cart</h1>
          <Link to="/shop" className={styles.BackLink}>Continue shopping</Link>
        </div>
        <CartView shopPath="/shop" />
      </>)}
      {!isCartPage && !id && (<div className={styles.PageHead}>
        <div>
          <h1 className={styles.PageTitle}>Shop Now</h1>
          <p className={styles.PageSub}>Order online and we deliver to your door.</p>
        </div>
      </div>)}
      {!isCartPage && (id ? <ProductDetail id={id} basePath="/shop" onAdd={onAdd} adding={addingId > 0} /> : <ProductCatalogue basePath="/shop" onAdd={onAdd} addingId={addingId} />)}
    </div>

    <CartDrawer shopPath="/shop" cartPath="/shop/cart" />

    <Snackbar open={error !== ""} autoHideDuration={5000} onClose={() => setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
      <Alert severity="error" variant="filled" onClose={() => setError("")}>{error}</Alert>
    </Snackbar>
  </div>);
};

export default Shop;
